import { useMemo } from 'react';
import { useQuery } from 'react-query';
import {
  fetchProductivityApps,
  fetchTeamProductivityApps,
} from '@services/productivityApps';
import { queryKeys } from '@config/queryKeys';

/**
 * Resolves the effective focus/distraction app lists: a team's override (if
 * one exists) wins entirely over the company defaults, otherwise the company
 * defaults apply. Both are `string[]` of app display names (matched
 * case-insensitively by callers).
 *
 * React Query — parallel callers with the same ids share one in-flight
 * request and cache entry (e.g. the dashboard's Focus widget and App
 * Usage's category column both mounting this hook costs one Firestore read
 * each, not two).
 *
 * @param {{ companyId?: string, teamId?: string }} ids
 * @returns {{ focusApps: string[], distractionApps: string[], isLoading: boolean }}
 */
export function useEffectiveProductivityApps({ companyId, teamId } = {}) {
  const { data: companyApps, isLoading: isLoadingCompany } = useQuery({
    queryKey: queryKeys.productivityApps.company(companyId),
    queryFn: () => fetchProductivityApps(companyId),
    enabled: !!companyId,
    staleTime: 5 * 60 * 1000,
    refetchOnWindowFocus: false,
  });

  const { data: teamApps } = useQuery({
    queryKey: queryKeys.productivityApps.team(teamId),
    queryFn: () => fetchTeamProductivityApps(teamId),
    enabled: !!teamId,
    staleTime: 5 * 60 * 1000,
    refetchOnWindowFocus: false,
  });

  const focusApps = useMemo(
    () =>
      teamApps?.hasOverride
        ? teamApps.focusApps
        : (companyApps?.focusApps ?? []),
    [teamApps, companyApps]
  );

  const distractionApps = useMemo(
    () =>
      teamApps?.hasOverride
        ? teamApps.distractionApps
        : (companyApps?.distractionApps ?? []),
    [teamApps, companyApps]
  );

  return {
    focusApps,
    distractionApps,
    isLoading: !!companyId && isLoadingCompany,
  };
}
